import { useCallback, useEffect, useMemo, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import Grid from '@zenbusiness/zui/Grid';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { useNavigate, generatePath } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import { MODAL_NAMES } from '#client/constants/modals';
import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import { TOKEN_TYPES } from '#client/constants/tokens';
import { PAYMENT_LIST_STATUS } from '#client/constants/transaction';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useBankingPath from '#client/hooks/useBankingPath';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';
import { UPDATE_COUNTER_PARTY_STATUS_MUTATION } from '#client/mutations';
import { formatAccountNumber } from '#client/utils/text';

import TransferTypeToolTip from './components/TransferTypeToolTip';
import { CaptionDescription, CaptionTitle, InfoContainer } from './PayeeInfo.styles';

import { ACH_TOOLTIP, WIRE_TOOLTIP } from './constants/tooltip';

export default function PayeeInfo({
  id,
  name,
  bank,
  accountNumber,
  status,
  accountType,
  connectedType,
  paymentStatus,
  fullAccountNumber,
  routingNumber,
  type,
  fullRoutingNumber,
  nickname,
  firstName,
  lastName,
  wire,
  loading,
  isWireSupported,
  isACHSupported
}) {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const { createSuccessToast, createErrorToast } = useToastContext();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const [counterPartyStatus, setCounterPartyStatus] = useState(status);
  const bankingPath = useBankingPath();

  const { id: bankingAccountId, loading: isFetchingBankingAccounts } = useBankingAccounts();

  useEffect(() => {
    setCounterPartyStatus(() => status);
  }, [status]);

  const isArchived = COUNTER_PARTY_STATUS.ARCHIVED === counterPartyStatus;

  const handleAfterDeletePayeeSuccessfully = useCallback(() => {
    navigate('..', { replace: true, relative: 'path' });
  }, [navigate]);

  const buttonPressedTracker = useButtonPressedCallback({
    elementName: 'Edit_Payee_added',
    elementText: 'Edit',
    appPageName: 'active_banking_dashboard'
  });

  const handleOpenEditPayeeModal = useCallback(() => {
    const counterPartyInfo = {
      id,
      name,
      bank,
      connectedType,
      accountType,
      fullAccountNumber,
      wire,
      type,
      routingNumber,
      accountNumber,
      isACHSupported,
      isWireSupported,
      fullRoutingNumber,
      nickname,
      firstName,
      lastName
    };

    /**
     * how many customers clicked edit payee
     */
    buttonPressedTracker();

    openModal({
      component: AddCounterPartyModal,
      mode: AddCounterPartyModal.MODAL_TYPES.EDIT_PAYEE,
      counterPartyInfo,
      deletePayeeCallback: handleAfterDeletePayeeSuccessfully
    });
  }, [
    accountNumber,
    accountType,
    bank,
    connectedType,
    firstName,
    fullAccountNumber,
    fullRoutingNumber,
    handleAfterDeletePayeeSuccessfully,
    id,
    isACHSupported,
    isWireSupported,
    lastName,
    name,
    nickname,
    openModal,
    routingNumber,
    type,
    wire,
    buttonPressedTracker
  ]);

  const [updateCounterparty, { loading: loadingUpdatePayeeStatus }] = useMutation(
    UPDATE_COUNTER_PARTY_STATUS_MUTATION,
    { noThrow: true }
  );

  const handleSetStatus = useCallback(async () => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      return;
    }

    const { data: responseData, error: updatePayeeError } = await updateCounterparty({
      variables: {
        input: {
          counterpartyId: id,
          customerToken,
          status: isArchived ? COUNTER_PARTY_STATUS.ACTIVE : COUNTER_PARTY_STATUS.ARCHIVED,
          bankingAccountId,
          idempotencyKey: uuidv4()
        }
      },
      skip: loadingUpdatePayeeStatus
    });

    if (updatePayeeError) {
      // Show toast error
      createErrorToast({
        title: 'Payee was not updated',
        description: 'There was an error updating this payee. Please try again.',
        error: updatePayeeError
      });
    } else {
      // show toast success and close modal
      const responseStatus = responseData?.updateBankingPartnerCounterparty?.status;
      const isActive = responseStatus === COUNTER_PARTY_STATUS.ACTIVE;
      createSuccessToast({
        title: `${name} has been ${isActive ? 'unarchived' : 'archived'}`,
        description: `${
          isActive
            ? `You can send money to ${name} again`
            : `You can still access your transaction history with ${name}`
        }`
      });
      setCounterPartyStatus(responseData?.updateBankingPartnerCounterparty?.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isArchived, loadingUpdatePayeeStatus, name]);

  const buttonText = useMemo(() => {
    const hasPayment = paymentStatus === PAYMENT_LIST_STATUS.HAS_PAYMENT;
    if (!hasPayment) {
      return 'Edit';
    }

    return isArchived ? 'Unarchive' : 'Archive';
  }, [paymentStatus, isArchived]);

  const handleClickSendMoneyButton = () => {
    openModal({
      name: MODAL_NAMES.SEND_MONEY_MODAL,
      entryPageForSendMoney: generatePath(`${bankingPath}/payees/:id`, {
        id
      }),
      counterParty: {
        id,
        name,
        bank,
        accountNumber,
        status,
        accountType,
        connectedType,
        fullAccountNumber,
        wire,
        isACHSupported,
        isWireSupported
      }
    });
  };

  const transferTypeValue = useMemo(() => {
    const value = [];
    if (isACHSupported) {
      value.push('ACH');
    }

    if (isWireSupported) {
      value.push('Wire');
    }

    return value;
  }, [isACHSupported, isWireSupported]);

  const disableMoveMoneyButton =
    isArchived || loadingUpdatePayeeStatus || loading || !counterPartyStatus;

  const isNotShowToolTip =
    loading ||
    paymentStatus === PAYMENT_LIST_STATUS.LOADING ||
    paymentStatus === PAYMENT_LIST_STATUS.EMPTY ||
    (isACHSupported && isWireSupported) ||
    (!isACHSupported && !isWireSupported);

  return (
    <Box mb="32px">
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        gap="16px"
        justifyContent="space-between"
        mb="32px"
      >
        <Stack>
          {isArchived && (
            <Text
              color="red"
              size="xs"
            >
              Archived
            </Text>
          )}
          {!name ? (
            <Skeleton width="200px" />
          ) : (
            <Heading
              family="secondary"
              size="3xl"
              weight="bold"
            >
              {name}
            </Heading>
          )}
        </Stack>
        <LoadingButton
          color="primary"
          disabled={disableMoveMoneyButton}
          disableRipple
          onClick={handleClickSendMoneyButton}
          variant="contained"
        >
          Send money
        </LoadingButton>
      </Stack>
      <InfoContainer
        direction="column"
        gap={{ xs: '32px', md: '48px' }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Heading
            size="2xl"
            weight="bold"
          >
            Info
          </Heading>
          {loading || !name || paymentStatus === PAYMENT_LIST_STATUS.LOADING ? (
            <Skeleton
              aria-label="skeleton"
              width="150px"
            />
          ) : (
            <LoadingButton
              color="info"
              disabled={loadingUpdatePayeeStatus || loading || isFetchingBankingAccounts}
              disableRipple
              onClick={
                paymentStatus === PAYMENT_LIST_STATUS.HAS_PAYMENT
                  ? handleSetStatus
                  : handleOpenEditPayeeModal
              }
              variant="text"
            >
              <Text
                color={
                  counterPartyStatus === COUNTER_PARTY_STATUS.ACTIVE &&
                  paymentStatus !== PAYMENT_LIST_STATUS.EMPTY
                    ? 'red'
                    : 'blue'
                }
                size="md"
                sx={{ opacity: loadingUpdatePayeeStatus || loading ? 0.5 : 1 }}
                weight="medium"
              >
                {buttonText}
              </Text>
            </LoadingButton>
          )}
        </Stack>
        <Grid
          columnSpacing={{ xs: 0, md: '32px' }}
          container
          rowSpacing={{ xs: '32px', md: 0 }}
        >
          <Grid
            item
            md={4}
            xs={12}
          >
            <Stack spacing={{ xs: '4px', md: '20px' }}>
              <CaptionTitle
                color={disableMoveMoneyButton ? 'tertiary' : 'disabled'}
                size="sm"
              >
                {isDesktop ? 'NAME' : 'Name'}
              </CaptionTitle>
              <CaptionDescription
                color={disableMoveMoneyButton ? 'tertiary' : 'primary'}
                size="xs"
              >
                {!name ? <Skeleton /> : name}
              </CaptionDescription>
            </Stack>
          </Grid>
          <Grid
            item
            md={4}
            xs={12}
          >
            <Stack spacing={{ xs: '4px', md: '20px' }}>
              <CaptionTitle
                color={disableMoveMoneyButton ? 'tertiary' : 'disabled'}
                size="sm"
              >
                {isDesktop ? 'ACCOUNT' : 'Account'}
              </CaptionTitle>
              <CaptionDescription
                color={disableMoveMoneyButton ? 'tertiary' : 'primary'}
                size="xs"
              >
                {!accountNumber ? (
                  <Skeleton />
                ) : (
                  [
                    bank,
                    formatAccountNumber({
                      accountNumber,
                      formatCharacter: '•',
                      numberFormatCharacter: 2
                    })
                  ].join(' ')
                )}
              </CaptionDescription>
            </Stack>
          </Grid>
          <Grid
            item
            md={4}
            xs={12}
          >
            <Stack spacing={{ xs: '4px', md: '20px' }}>
              <Stack
                direction="row"
                spacing="4px"
              >
                <CaptionTitle
                  color={disableMoveMoneyButton ? 'tertiary' : 'disabled'}
                  size="sm"
                >
                  {isDesktop ? 'TRANSFER TYPES' : 'Transfer types'}
                </CaptionTitle>
                {!isNotShowToolTip && (
                  <TransferTypeToolTip
                    description={
                      isACHSupported ? ACH_TOOLTIP.description : WIRE_TOOLTIP.description
                    }
                    title={isACHSupported ? ACH_TOOLTIP.title : WIRE_TOOLTIP.title}
                  />
                )}
              </Stack>
              <CaptionDescription
                color={disableMoveMoneyButton ? 'tertiary' : 'primary'}
                size="lg"
              >
                {!name ? <Skeleton /> : transferTypeValue.join(', ')}
              </CaptionDescription>
            </Stack>
          </Grid>
        </Grid>
      </InfoContainer>
    </Box>
  );
}

PayeeInfo.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  bank: PropTypes.string,
  accountNumber: PropTypes.string,
  status: PropTypes.string,
  connectedType: PropTypes.string,
  accountType: PropTypes.string,
  fullAccountNumber: PropTypes.string,
  isWireSupported: PropTypes.bool,
  isACHSupported: PropTypes.bool,
  type: PropTypes.string,
  routingNumber: PropTypes.string,
  fullRoutingNumber: PropTypes.string,
  nickname: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  wire: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      state: PropTypes.string,
      street: PropTypes.string,
      street2: PropTypes.string
    }),
    routingNumber: PropTypes.string
  }),
  loading: PropTypes.bool,
  paymentStatus: PropTypes.string
};
